import React from 'react';
import moment from 'moment-timezone';
import { Button, Card, Popover, Tooltip } from 'antd';
import { connect } from 'react-redux';
import { fetchWalkthroughsForHeatmap } from '../../thunks/walkthroughs_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getWalkthroughScoreObject, nullSafeGet } from '../../utils/DataAccessUtils';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { withUserType } from '../withUserType';
import DateTimeHover from '../date_time_component/DateTime';
import { generateHeatmapGrid, Score } from './heatmapUtils';

export { generateHeatmapGrid } from './heatmapUtils';

const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const scoreToColor: Record<Score, string> = {
	'No score': 'bg-gray-200',
	'less than 50%': 'bg-red-400',
	'50% - 99%': 'bg-yellow-400',
	'100%': 'bg-green-500',
};
type GroupedByDate = Record<
	string,
	{
		walkthroughs: any[];
		totalScore: number;
		totalMax: number;
		totalPercentage: number;
		count: number;
	}
>;

const transformWalkthroughsToHeatmapData = (walkthroughs, timeZoneId) => {
	const groupedByDate: GroupedByDate = walkthroughs
		.filter((wt) => wt.completedAt)
		.reduce((acc, wt) => {
			// Bucket by the same zone the cell popover renders in, so a
			// walkthrough completed near midnight lands in the day its
			// displayed timestamp shows (not the viewer's browser day).
			const date = (
				timeZoneId ? moment.tz(wt.completedAt, timeZoneId) : moment(wt.completedAt)
			).format('YYYY-MM-DD');
			// Reads the nested scores array when present and falls back to the flat overall* fields on
			// OpenSearch-backed walkthroughs (which no longer carry the scores array).
			const overallScore = getWalkthroughScoreObject(wt);

			const score = parseFloat(nullSafeGet('score', overallScore) || '0');
			const maxPossibleScore = parseFloat(nullSafeGet('maxPossibleScore', overallScore) || '0');
			const percentageScore = parseFloat(nullSafeGet('percentageScore', overallScore) || '0');

			if (!acc[date]) {
				acc[date] = {
					walkthroughs: [],
					totalScore: 0,
					totalMax: 0,
					totalPercentage: 0,
					count: 0,
				};
			}

			acc[date].walkthroughs.push({ ...wt, score, maxPossibleScore, percentageScore });
			acc[date].totalScore += score;
			acc[date].totalMax += maxPossibleScore;
			acc[date].totalPercentage += percentageScore;
			acc[date].count += 1;

			return acc;
		}, {});

	// Build the data map with average scores
	const dataMap = Object.entries(groupedByDate).reduce((acc, [date, group]) => {
		const avgScore = group.totalScore / group.count;
		const avgMax = group.totalMax / group.count;
		const avgPercentage = group.totalPercentage / group.count;

		let scoreLabel: Score = 'No score';
		if (avgScore === 0) scoreLabel = 'No score';
		else if (avgScore === avgMax) scoreLabel = '100%';
		else if (avgScore >= avgMax * 0.5) scoreLabel = '50% - 99%';
		else scoreLabel = 'less than 50%';

		acc[date] = {
			score: scoreLabel,
			percentageScore: avgPercentage.toFixed(1),
			walkthroughs: group.walkthroughs,
		};

		return acc;
	}, {});

	return dataMap;
};

const WalkthroughHeatmap = ({
	getWalkthroughData,
	siteSurveyId,
	startDate,
	endDate,
	userType,
	timeZoneId,
}) => {
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState<string | null>(null);
	const [gridData, setGridData] = React.useState([]);
	const [monthLabels, setMonthLabels] = React.useState([]);

	React.useEffect(() => {
		setLoading(true);
		setError(null);
		getWalkthroughData(siteSurveyId, startDate, endDate)
			.then((data) => {
				const dataMap = transformWalkthroughsToHeatmapData(data, timeZoneId); // new structure
				const { grid, monthLabels } = generateHeatmapGrid(startDate, endDate, dataMap);
				setGridData(grid);
				setMonthLabels(monthLabels);
			})
			.catch((err) => {
				setError(err.message);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [siteSurveyId, timeZoneId]);

	if (error || !nullSafeGet('length', gridData)) return null;

	return (
		<Card
			title={`Walkthroughs score for last 6 months`}
			loading={loading}
			style={{ height: '100%' }}
		>
			<div className="flex flex-col justify-between">
				<div className="flex flex-col ">
					{/* Month Labels — ml-7 origin matches the weekday-label column (w-6 24px + mr-1 4px) */}
					<div className="relative mb-2 ml-7 h-5">
						{monthLabels.map(({ label, colIndexStart, colIndexEnd }) => {
							const columnWidth = 28; // px, column pitch = w-6 (24) + gap-1 (4)
							const center = (colIndexStart + colIndexEnd + 1) / 2;
							const left = center * columnWidth;

							return (
								<div
									key={label}
									className="absolute text-xs text-gray-600"
									style={{
										left: `${left}px`,
										transform: 'translateX(-50%)',
									}}
								>
									{label}
								</div>
							);
						})}
					</div>

					<div className="flex">
						{/* Weekday Labels */}
						<div className="mr-1 flex flex-col gap-1 text-xs text-gray-500">
							{dayLabels.map((day) => (
								<div key={day} className="h-6 w-6">
									{day}
								</div>
							))}
						</div>

						{/* Grid */}
						<div className="flex gap-1">
							{gridData.map((week, i) => (
								<div key={i} className="flex flex-col gap-1">
									{week.map((cell) => (
										<Popover
											key={cell.date}
											title={moment(cell.date).format('MMM Do')}
											content={
												<div className="space-y-2 text-sm text-gray-700">
													{parseInt(cell.percentageScore) ? (
														<div>
															<span className="font-semibold">
																{cell.walkthroughs.length > 1 ? 'Average Score' : 'Score'}:
															</span>{' '}
															{cell.percentageScore}%
														</div>
													) : (
														<div>No score available</div>
													)}
													<div className="space-y-2">
														{cell.walkthroughs.map((wt, idx) => (
															<div key={wt.id} className="border-b pb-2 last:border-b-0">
																<Link
																	to={`/${userType}/siteSurveys/walkthrough/detail/${wt.id}/details`}
																	className="flex flex-col text-blue-600 hover:underline"
																>
																	<span className="font-medium">
																		{idx + 1}. {wt.title || wt.name || 'Walkthrough'}
																	</span>
																	<span className="text-xs text-gray-500">
																		<DateTimeHover
																			timestamp={wt.completedAt}
																			dateFormat={'MMM Do, h:mm A z'}
																			showDate
																			timeZone={timeZoneId}
																		/>
																		<br />
																		{parseInt(cell.percentageScore) ? (
																			<>
																				Score:
																				<span className="font-semibold">{wt.percentageScore}%</span>
																			</>
																		) : null}
																	</span>
																</Link>
															</div>
														))}
													</div>
												</div>
											}
										>
											<div className={`h-6 w-6 rounded ${scoreToColor[cell.score]}`} />
										</Popover>
									))}
								</div>
							))}
						</div>
					</div>
				</div>

				{/* Legend */}

				<div className="ml-10 mt-4 flex items-center gap-4 text-sm">
					{(['No score', 'less than 50%', '50% - 99%', '100%'] as Score[]).map((score) => (
						<div key={score} className="flex items-center gap-1">
							<div className={`h-6 w-6 rounded ${scoreToColor[score]}`} />
							<span>{score}</span>
						</div>
					))}
				</div>
			</div>
		</Card>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	getWalkthroughData: (siteSurveyId, startDate, endDate) =>
		dispatch(fetchWalkthroughsForHeatmap(ownProps.userType)(siteSurveyId, startDate, endDate)),
});

export default withUserType(connect(null, mapDispatchToProps)(WalkthroughHeatmap));
